import * as React from 'react';
import Flatpickr from 'react-flatpickr';
import {FieldError} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {CalendarOutlined, CloseOutlined} from '@ant-design/icons';
import {Button} from 'antd';
import classNames from 'classnames';
import {getErrorMessage} from 'helpers/utils';

export interface DateInputProps {
  id?: string;
  label?: string;
  value?: Date | null;
  disabled?: boolean;
  disabledArr?: Date[];
  className?: string;
  pickerClassName?: string;
  onChange?: (date: Date) => void;
  showIcons?: boolean;
  required?: boolean;
  error?: FieldError | FieldError[];
}

export const DateInput: React.FC<DateInputProps> = (props) => {
  const {t} = useTranslation();
  const {
    id,
    label,
    value,
    disabled,
    disabledArr,
    showIcons = true,
    className,
    pickerClassName,
    required,
    onChange,
    error,
  } = props;
  const errorMessage = error ? getErrorMessage(error, label) : null;

  const inputRef: React.LegacyRef<Flatpickr> = React.useRef(null);

  const [inputValue, setInputValue] = React.useState<Date | null>(null);

  return (
    <div className={classNames('text-input-wrapper', className)}>
      {label && (
        <label htmlFor={id}>
          {label} {required && <span>*</span>}
        </label>
      )}
      <div className="relative">
        <Flatpickr
          id={id}
          ref={inputRef}
          className={classNames(
            errorMessage ? 'text-input--error' : 'text-input',
            pickerClassName,
            disabled && 'text-antdDisabled'
          )}
          disabled={disabled}
          placeholder={t('Select date...')}
          value={value ?? undefined}
          onChange={(date) => {
            setInputValue(date[0]);
            onChange && onChange(date[0]);
          }}
          options={{
            enableTime: false,
            disable: disabledArr ?? [],
            dateFormat: 'm/d/Y',
            clickOpens: true,
            disableMobile: true,
          }}
        />
        <div className="absolute h-11 top-0 right-0 pr-1 flex items-center gap-2">
          {inputValue && showIcons && (
            <Button
              type="link"
              onClick={() => inputRef.current?.flatpickr.clear()}
              icon={<CloseOutlined className="text-lg" />}
            />
          )}
          {showIcons && (
            <Button
              type="link"
              onClick={() => inputRef.current?.flatpickr.open()}
              icon={<CalendarOutlined className={classNames('text-lg', disabled && 'text-antdDisabled')} />}
            />
          )}
        </div>
      </div>
      {errorMessage && <p className="input-error-message">{errorMessage}</p>}
    </div>
  );
};
